import { createSignal } from "solid-js";
import type { ChannelKey } from "./channelKey";
import { formatDateTime } from "./dateFormat";
import { identityScopedStore } from "./identityScopedStore";

// Per-channel topic + modes store. Module-singleton reactive signals.
//
// Topic state is pushed by the server via `topic_changed` events on the
// channel-level Phoenix Channel topic. Modes are pushed via
// `channel_modes_changed`. Both are consumed in `subscribe.ts` and
// routed here via `seedTopic` / `seedModes`.
//
// `compactModeString` is a pure function exported for use in TopicBar and
// tests alike — formats `["n", "t"]` → `"+nt"`.
//
// Lifecycle (#975). Both maps hold state that is only true WHILE the
// session is in the channel, so both are emptied at the two boundaries
// where that stops being true:
//   * own-PART → `dropChannelTopicState(key)` from subscribe.ts, beside
//     the `setParted(key)` windowState projection.
//   * logout / bearer rotation → the `identityScopedStore` resets below,
//     like scrollback.ts / selection.ts / members.ts / readCursor.ts.
// Pre-#975 there was NO emptier at all: the pre-PART entry survived
// forever and ModeModal presented it as the channel's CURRENT modes. The
// old comment here claimed no cleanup was needed because "the next join
// pushes fresh state" — true, but it answers the wrong question: between
// the part and any re-join the cache is a confident lie. Absence is the
// honest answer, and it is what ModeModal now renders as "modes unknown".
//
// UX-5 BJ (2026-05-19): the per-channel creation timestamp store
// (329 RPL_CREATIONTIME → `createdByChannel` / `seedChannelCreated`)
// was removed. Its only consumer was the JOIN-self banner, which BJ
// killed (topic + members already surface via TopicBar + MembersPane;
// the banner duplicated both). The corresponding `channel_created`
// wire-event arm survives as a recognized-but-ignored no-op in
// `subscribe.ts` (server still emits per-channel on every JOIN; killing
// the narrow arm would route the payload through the default-null
// drop and log `[subscribe] dropped malformed payload` on every JOIN).
// Server-side reaping of the broadcast itself is a separate decision.

export type TopicEntry = {
  text: string | null;
  set_by: string | null;
  set_at: string | null;
};

export type ModesEntry = {
  modes: string[];
  params: Record<string, string | null>;
};

/**
 * #1505 — "this channel has no topic", the ONE predicate every surface asks.
 *
 * The server publishes that fact in TWO shapes and both are truthful:
 * `null` is 331 RPL_NOTOPIC (or a topic that has not arrived yet), `""` is
 * what an operator's `TOPIC #chan :` actually put on the wire — the same
 * empty trailing `Client.send_topic_clear/2` writes for our own
 * `/topic -delete`. #1500 pinned `""` over `NULL` for the stored body on the
 * rule that the wire carried an empty string, not an absence; the server
 * therefore does NOT normalise the two into one, and the READ side is where
 * they converge.
 *
 * Whitespace is deliberately NOT trimmed: a topic of spaces is a topic the
 * wire carried, so `" "` is present, not absent. `topicJoinLine` is stricter
 * on purpose and that divergence is presentational, not a second answer to
 * this question — it decides whether to PRINT a join-time line at all, and a
 * line that would render blank is worth nothing there. The bar has no such
 * option: it occupies its strip either way, so it must say something true.
 */
export function hasNoTopic(text: string | null | undefined): boolean {
  return text === null || text === undefined || text === "";
}

const exports_ = identityScopedStore((onIdentityChange) => {
  const [topicByChannel, setTopicByChannel] = createSignal<Record<ChannelKey, TopicEntry>>({});
  const [modesByChannel, setModesByChannel] = createSignal<Record<ChannelKey, ModesEntry>>({});

  onIdentityChange(() => setTopicByChannel({}));
  onIdentityChange(() => setModesByChannel({}));

  const dropKey = <T>(key: ChannelKey) => {
    return (prev: Record<ChannelKey, T>): Record<ChannelKey, T> => {
      if (!(key in prev)) return prev;
      const { [key]: _drop, ...rest } = prev;
      return rest;
    };
  };

  const seedTopic = (key: ChannelKey, entry: TopicEntry): void => {
    setTopicByChannel((prev) => ({ ...prev, [key]: entry }));
  };

  const seedModes = (key: ChannelKey, entry: ModesEntry): void => {
    setModesByChannel((prev) => ({ ...prev, [key]: entry }));
  };

  // Both maps go together: topic and modes are the same datum class
  // (server-pushed while joined, unobservable once parted) and splitting
  // their lifecycle would leave two twin structures behaving differently
  // for no reason a later reader could recover. Idempotent — dropping an
  // unknown key returns the same object, so no consumer wakes for nothing.
  const dropChannelTopicState = (key: ChannelKey): void => {
    setTopicByChannel(dropKey(key));
    setModesByChannel(dropKey(key));
  };

  return {
    topicByChannel,
    modesByChannel,
    seedTopic,
    seedModes,
    dropChannelTopicState,
  };
});

export const topicByChannel = exports_.topicByChannel;
export const modesByChannel = exports_.modesByChannel;
export const seedTopic = exports_.seedTopic;
export const seedModes = exports_.seedModes;
export const dropChannelTopicState = exports_.dropChannelTopicState;

/**
 * #263 — flattens a multi-line topic draft to a SINGLE wire line.
 *
 * An IRC topic is one line; CRLF is the message terminator. The #263 modal
 * editor is a multi-line `<textarea>`, but the server REJECTS a topic body
 * containing `\r`/`\n`/`\x00` outright (`Identifier.safe_line_token?/1` →
 * `:invalid_line`) — so flattening on submit is MANDATORY, not cosmetic: a raw
 * multi-line submit would always fail the save. Every newline run (`\r\n`,
 * `\r`, `\n`, and consecutive blank lines) collapses to a SINGLE space so
 * words on separate lines stay separated rather than fused. `\x00` is a
 * distinct guard concern but a textarea cannot produce it, so it is out of
 * scope here.
 */
export function flattenTopicNewlines(text: string): string {
  return text.replace(/[\r\n]+/g, " ");
}

/**
 * Formats a modes array into a compact IRC mode string.
 * `["n", "t"]` → `"+nt"`. Empty array → `""`.
 */
export function compactModeString(modes: string[]): string {
  if (modes.length === 0) return "";
  return `+${modes.join("")}`;
}

// #237 — the on-JOIN inline topic line, DERIVED from this store (no parallel
// state, no faked scrollback id). `topicByChannel` is already seeded by the
// server's `topic_changed` event on JOIN (RPL_TOPIC 332 → full text + setter +
// time) and on every change; ScrollbackPane reads these pure helpers to render
// a PRESENTATIONAL buffer row anchored to the own-JOIN, irssi-style. Kept pure
// (no signal reads) so the derivation is unit-testable without rendering.
//
// on-JOIN vs on-CHANGE split: the server persists a real `:topic` scrollback
// row ONLY on a mid-session TOPIC change (rendered by ScrollbackPane's
// `case "topic"`). JOIN emits topic_changed WITHOUT a scrollback row (avoids
// reconnect spam), so the join-time inline print is derived HERE from the
// cached entry — it shows the CURRENT cached topic (last-write-wins), not a
// frozen topic-at-join snapshot (cic holds no per-join topic history). See
// docs/DESIGN_NOTES.md 2026-07-15.

/** The renderable on-JOIN topic line: channel + full topic text + optional
 * "set by <nick> at <time>" meta. */
export type TopicJoinLine = {
  channel: string;
  /** Full topic text VERBATIM (mIRC control bytes preserved for MircBody). */
  text: string;
  meta: string | null;
};

/**
 * Maps a channel + its cached topic entry to the on-JOIN inline line, or
 * `null` when there is nothing to print (no entry, explicit no-topic, or a
 * blank/whitespace-only topic — mirrors irssi printing nothing on join for a
 * topicless channel).
 */
export function topicJoinLine(channel: string, entry: TopicEntry | null): TopicJoinLine | null {
  const text = entry?.text ?? null;
  if (text === null || text.trim() === "") return null;
  return { channel, text, meta: topicJoinMeta(entry) };
}

/**
 * Formats the irssi-style "set by <nick> at <time>" suffix from a topic entry.
 * `null` when the setter is unknown (a 332 that arrived without a 333). The
 * set-at time is dropped when absent, and falls back to the raw string when
 * unparseable (never leaks a JS "Invalid Date" to the user).
 */
export function topicJoinMeta(entry: TopicEntry | null): string | null {
  const setBy = entry?.set_by ?? null;
  if (!setBy) return null;
  const setAt = entry?.set_at ?? null;
  return setAt ? `set by ${setBy} at ${formatTopicSetAt(setAt)}` : `set by ${setBy}`;
}

// issue 2270 — through the ONE date renderer. The NaN guard above stays where
// it is: it is what keeps an unparseable upstream string reaching the user
// verbatim instead of as "Invalid Date".
function formatTopicSetAt(setAt: string): string {
  const parsed = new Date(setAt);
  if (Number.isNaN(parsed.getTime())) return setAt;
  return formatDateTime(parsed.getTime());
}

// #1914 — the `/topic` answer line. Sibling of `topicJoinLine` above, and the
// difference between them is the whole reason it is a second function rather
// than a flag: the JOIN line is UNSOLICITED, so it prints nothing when there
// is nothing worth printing; this one answers a question the operator just
// asked, so it must always say something true — including "no topic set",
// which is an answer and not an absence.
//
// `text: null` carries that case. Both `hasNoTopic` shapes (331's `null` and
// an operator's `TOPIC #chan :` empty string) collapse into it here, because
// the operator asked what the topic IS and both mean "there isn't one"; the
// distinction between them is a wire fact, not a fact about the channel.

/** The renderable `/topic` answer: `text: null` means "no topic set". */
export type TopicShowLine = {
  channel: string;
  /** Full topic text VERBATIM (mIRC control bytes preserved for MircBody),
   * or `null` when the channel has no topic. */
  text: string | null;
  meta: string | null;
};

/**
 * Maps a channel + its frozen topic entry to the `/topic` answer line. Never
 * returns null — an answer is always printed, unlike `topicJoinLine`.
 *
 * A whitespace-only topic is a topic the wire carried (the `hasNoTopic`
 * posture), so it renders as one rather than collapsing to "no topic set".
 */
export function topicShowLine(channel: string, entry: TopicEntry): TopicShowLine {
  const text = entry.text ?? null;
  return {
    channel,
    text: hasNoTopic(text) ? null : text,
    meta: hasNoTopic(text) ? null : topicJoinMeta(entry),
  };
}
